import { Form, Input, Select, Switch, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { FORM_TYPES, type FormTypeValue } from '../constants';

const { Text } = Typography;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface FormMetaValues {
  title: string;
  slug: string;
  formType: FormTypeValue;
  emailRecipients: string[];
  kvkkText: string;
  captchaEnabled: boolean;
}

interface FormMetaPanelProps {
  values: FormMetaValues;
  onChange: (patch: Partial<FormMetaValues>) => void;
  disabled?: boolean;
}

/** Form ayarları — başlık/slug/tip/e-posta alıcıları/KVKK/captcha. Alan tasarımından ayrı. */
export function FormMetaPanel({ values, onChange, disabled }: FormMetaPanelProps) {
  const { t } = useTranslation('forms');
  const td = t as unknown as (key: string) => string;

  const handleEmailChange = (next: string[]) => {
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const raw of next) {
      const email = raw.trim();
      if (!email) continue;
      if (EMAIL_RE.test(email)) valid.push(email);
      else invalid.push(email);
    }
    if (invalid.length > 0) {
      void message.warning(t('fields.emailInvalid', { value: invalid.join(', ') }));
    }
    onChange({ emailRecipients: Array.from(new Set(valid)) });
  };

  return (
    <Form layout="vertical" disabled={disabled}>
      <Form.Item label={t('fields.title')} required style={{ marginBottom: 12 }}>
        <Input
          value={values.title}
          maxLength={200}
          onChange={(e) => onChange({ title: e.target.value })}
          data-testid="form-meta-title"
        />
      </Form.Item>

      <Form.Item label={t('fields.slug')} style={{ marginBottom: 12 }}>
        <SlugInput
          value={values.slug}
          sourceValue={values.title}
          onChange={(slug) => onChange({ slug })}
          disabled={disabled}
        />
      </Form.Item>

      <Form.Item label={t('fields.type')} style={{ marginBottom: 12 }}>
        <Select<FormTypeValue>
          value={values.formType}
          onChange={(formType) => onChange({ formType })}
          options={FORM_TYPES.map((type) => ({ value: type, label: td(`type.${type}`) }))}
          data-testid="form-meta-type"
        />
      </Form.Item>

      <Form.Item
        label={t('fields.emailRecipients')}
        help={t('fields.emailRecipientsHint')}
        style={{ marginBottom: 12 }}
      >
        <Select
          mode="tags"
          value={values.emailRecipients}
          onChange={handleEmailChange}
          tokenSeparators={[',', ' ', ';']}
          placeholder={t('fields.emailPlaceholder')}
          open={false}
          suffixIcon={null}
          data-testid="form-meta-emails"
        />
      </Form.Item>

      <Form.Item label={t('fields.kvkkText')} help={t('fields.kvkkTextHint')} style={{ marginBottom: 12 }}>
        <Input.TextArea
          value={values.kvkkText}
          onChange={(e) => onChange({ kvkkText: e.target.value })}
          rows={4}
          placeholder={t('fields.kvkkPlaceholder')}
          data-testid="form-meta-kvkk"
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Switch
            checked={values.captchaEnabled}
            onChange={(captchaEnabled) => onChange({ captchaEnabled })}
            data-testid="form-meta-captcha"
          />
          <div>
            <Text>{t('fields.captchaEnabled')}</Text>
            <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)' }}>{t('fields.captchaHint')}</div>
          </div>
        </div>
      </Form.Item>
    </Form>
  );
}
